import { apiPHP } from './api';
import type { IHuntWordData } from '../games/HuntWords/huntWordInterface';

// Camada de acesso aos endpoints do painel admin (games-rec). Espelha os DTOs
// do backend. Datas trafegam como string ISO; valorAVista como string (Decimal).

/**
 * Detalhe de um erro para compor uma mensagem de mais alto nível — MESMA precedência
 * do `errMsg` do EditorShell (response.data.msg → e.message → genérico), porque é a
 * mesma pergunta: "o que dá pra mostrar de útil sobre isso?". Sem isto, todo embrulho
 * (`new Error(\`... ${detail}\`)`) que lesse só `err.message` jogava fora o
 * `response.data.msg` de um erro do axios — cujo `.message` é sempre o genérico
 * "Request failed with status code N", nunca o texto do servidor — e assava esse
 * genérico DENTRO do embrulho, onde o `errMsg` não tem mais como recuperar o original.
 */
export function detailOf(err: unknown, fallback = 'erro desconhecido'): string {
  const data = (err as { response?: { data?: { msg?: string } } } | null)?.response?.data;
  if (data?.msg) return data.msg;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** Tipo da edição: comum ou Treinamento (Academia da Mente). */
export type EditionKind = 'DEFAULT' | 'MIND_TRAINING';

/** Área cognitiva de um jogo da Academia da Mente. */
export type CognitiveDomain = 'SPEED' | 'MEMORY' | 'REASONING' | 'LANGUAGE';

export interface EdicaoAdmin {
  id: number;
  nome: string | null;
  subtitulo: string | null;
  descricao: string | null;
  dataPublicacao: string | null;
  valorAVista: string | null;
  aVenda: boolean | null;
  liberadaGratis: boolean | null;
  torneioInicio: string | null;
  torneioFim: string | null;
  dificuldadesId: number | null;
  author: string | null;
  city: string | null;
  state: string | null;
  qtdPassatempos: number;
  categoryIds: number[];
  // Ausente na API antiga → tratar como 'DEFAULT'.
  kind?: EditionKind;
}

/** Corpo de criação/edição de edição — só os campos preenchidos são enviados. */
export interface EdicaoWritePayload {
  nome?: string;
  subtitulo?: string;
  descricao?: string;
  dataPublicacao?: string;
  valorAVista?: number;
  aVenda?: boolean;
  liberadaGratis?: boolean;
  torneioInicio?: string;
  torneioFim?: string;
  dificuldadesId?: number;
  author?: string;
  city?: string;
  state?: string;
  categoryIds?: number[];
  kind?: EditionKind;
}

export interface CategoryAdmin {
  id: number;
  name: string;
  tagline: string | null;
  position: number;
  active: boolean;
  showInOnboarding: boolean;
  editionCount: number;
}

export interface CategoryWritePayload {
  name?: string;
  tagline?: string | null;
  active?: boolean;
  showInOnboarding?: boolean;
}

export const listCategoriesAdmin = (): Promise<CategoryAdmin[]> =>
  apiPHP.get<CategoryAdmin[]>('/admin/categorias').then((r) => r.data);

export const createCategory = (p: CategoryWritePayload): Promise<CategoryAdmin> =>
  apiPHP.post<CategoryAdmin>('/admin/categorias', p).then((r) => r.data);

export const updateCategory = (id: number, p: CategoryWritePayload): Promise<CategoryAdmin> =>
  apiPHP.patch<CategoryAdmin>(`/admin/categorias/${id}`, p).then((r) => r.data);

export const deleteCategory = (id: number): Promise<void> =>
  apiPHP.delete(`/admin/categorias/${id}`).then(() => undefined);

export const reorderCategories = (ids: number[]): Promise<CategoryAdmin[]> =>
  apiPHP.put<CategoryAdmin[]>('/admin/categorias/ordem', { ids }).then((r) => r.data);

export interface Tema {
  id: number;
  nome: string | null;
  edicoesId: number | null;
}

export interface TemaWritePayload {
  nome?: string;
  edicoesId?: number;
}

export interface TipoDePassatempo {
  id: number;
  nome: string | null;
  nomeFantasia: string | null;
  ordem: number | null;
}

// A tabela tipos_de_passatempos mistura cruzadas com sudoku/rebus/quiz/caça, e o
// `nomeFantasia` está sobrecarregado com rótulos de marketing (ex.: "sopa de letras"
// aparece como "Cruzadas Temáticas - Enem"). O editor de cruzada só deve oferecer os
// tipos da FAMÍLIA cruzada — filtramos pelo `nome` técnico, que é confiável.
export const CROSSWORD_TIPO_NOMES = ['normal', 'mini', 'cross-quiz', 'glaucoma'];

export const isCrosswordTipo = (t: TipoDePassatempo): boolean =>
  t.nome != null && CROSSWORD_TIPO_NOMES.includes(t.nome);

export interface EstiloDePassatempo {
  id: number;
  nome: string | null;
}

export interface Dificuldade {
  id: number;
  nome: string;
}

// ---- Edições ----
export const listEdicoes = (): Promise<EdicaoAdmin[]> =>
  apiPHP.get<EdicaoAdmin[]>('/admin/edicoes').then((r) => r.data);

export const createEdicao = (payload: EdicaoWritePayload): Promise<EdicaoAdmin> =>
  apiPHP.post<EdicaoAdmin>('/jogo/edicoes', payload).then((r) => r.data);

export const updateEdicao = (id: number, payload: EdicaoWritePayload): Promise<EdicaoAdmin> =>
  apiPHP.patch<EdicaoAdmin>(`/jogo/edicoes/${id}`, payload).then((r) => r.data);

export const deleteEdicao = (id: number): Promise<void> =>
  apiPHP.delete(`/jogo/edicoes/${id}`).then(() => undefined);

// ---- Temas ----
export const listTemas = (edicoesId?: number): Promise<Tema[]> =>
  apiPHP
    .get<Tema[]>('/jogo/temas', { params: edicoesId === undefined ? {} : { edicoesId } })
    .then((r) => r.data);

export const createTema = (payload: TemaWritePayload): Promise<Tema> =>
  apiPHP.post<Tema>('/jogo/temas', payload).then((r) => r.data);

export const updateTema = (id: number, payload: TemaWritePayload): Promise<Tema> =>
  apiPHP.patch<Tema>(`/jogo/temas/${id}`, payload).then((r) => r.data);

export const deleteTema = (id: number): Promise<void> =>
  apiPHP.delete(`/jogo/temas/${id}`).then(() => undefined);

// ---- Listas de referência (dropdowns) ----
export const listTipos = (): Promise<TipoDePassatempo[]> =>
  apiPHP.get<TipoDePassatempo[]>('/tipos-de-passatempos').then((r) => r.data);

export const listEstilos = (): Promise<EstiloDePassatempo[]> =>
  apiPHP.get<EstiloDePassatempo[]>('/estilos-de-passatempos').then((r) => r.data);

export const listDificuldades = (): Promise<Dificuldade[]> =>
  apiPHP.get<Dificuldade[]>('/dificuldades').then((r) => r.data);

// ---- Paginação ----
export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ---- Passatempos ----
export interface PassatempoAdmin {
  id: number;
  titulo: string | null;
  subtitulo: string | null;
  edicoesId: number | null;
  temasPassatemposId: number | null;
  tiposDePassatemposId: number | null;
  // O catálogo usa isto para o gating: estilo 3 (cruzada direta) → "Editar grade";
  // os demais → "Regerar". Exposto pelo backend em games-rec#29.
  estilosDePassatemposId: number | null;
  dificuldadesId: number | null;
  doDia: string | null;
  cognitiveDomain?: CognitiveDomain | null;
  academyPoints?: number | null;
}

export interface PassatempoUpdatePayload {
  titulo?: string;
  subtitulo?: string;
  edicoesId?: number;
  temasPassatemposId?: number;
  tiposDePassatemposId?: number;
  estilosDePassatemposId?: number;
  dificuldadesId?: number;
  cognitiveDomain?: CognitiveDomain | null;
  academyPoints?: number | null;
}

export interface ListPassatemposParams {
  page?: number;
  pageSize?: number;
  q?: string;
  edicoesId?: number;
  temasPassatemposId?: number;
  tiposDePassatemposId?: number;
}

export const listPassatempos = (
  params: ListPassatemposParams = {},
): Promise<PaginatedResult<PassatempoAdmin>> =>
  apiPHP.get<PaginatedResult<PassatempoAdmin>>('/admin/passatempos', { params }).then((r) => r.data);

export const updatePassatempo = (
  id: number,
  payload: PassatempoUpdatePayload,
): Promise<PassatempoAdmin> =>
  apiPHP.patch<PassatempoAdmin>(`/admin/passatempos/${id}`, payload).then((r) => r.data);

export const deletePassatempo = (id: number): Promise<void> =>
  apiPHP.delete(`/admin/passatempos/${id}`).then(() => undefined);

export const regenerateCrossword = (
  id: number,
  body: { titulo?: string; itens: { palavra: string; dica?: string }[] },
): Promise<{ colocadas: string[]; naoColocadas: string[] }> =>
  apiPHP.post(`/admin/passatempos/${id}/regenerate`, body).then((r) => r.data);

// ---- Editor de cruzada direta (games-rec#29) ----
// O cliente autora com `ref` temporários; o servidor materializa os ids. As casas
// mortas apontam para refs. `indices` (ref → número) volta do servidor para o editor
// conferir contra o seu deriveIndices e avisar se divergir.

export interface CrosswordWordPayload {
  ref: string;
  texto: string;
  x: number;
  y: number;
  direcao: boolean; // false = horizontal, true = vertical
  chave: string;
}

export interface CrosswordDeadCellPayload {
  x: number;
  y: number;
  visivel?: boolean;
  palavra1Ref?: string;
  palavra2Ref?: string;
}

export interface CrosswordHighlightPayload {
  x: number;
  y: number;
}

export interface CrosswordDraftPayload {
  titulo?: string;
  largura: number;
  altura: number;
  tiposDePassatemposId: number;
  palavras: CrosswordWordPayload[];
  casasMortas: CrosswordDeadCellPayload[];
  casasDestaques?: CrosswordHighlightPayload[];
}

export interface CrosswordSaveResult {
  id: number;
  indices: Record<string, number>;
}

export interface CrosswordPreviewResult {
  indices: Record<string, number>;
}

export interface CrosswordSourceWord {
  id: number;
  texto: string;
  x: number;
  y: number;
  direcao: boolean;
  chave: string;
}

export interface CrosswordSourceDeadCell {
  x: number;
  y: number;
  visivel: boolean;
  palavras1Id: number | null;
  palavras2Id: number | null;
}

export interface CrosswordSource {
  id: number;
  titulo: string | null;
  largura: number;
  altura: number;
  estilosDePassatemposId: number | null;
  tiposDePassatemposId: number | null;
  palavras: CrosswordSourceWord[];
  casasMortas: CrosswordSourceDeadCell[];
  casasDestaques: CrosswordHighlightPayload[];
  progressosCount: number;
  rankingsCount: number;
  /** false quando já há ranking: editar a grade trancaria quem já pontuou fora do
   *  ranking para sempre. A saída é duplicar. O PUT reforça com 409. */
  editavel: boolean;
}

export const getCrosswordSource = (id: number): Promise<CrosswordSource> =>
  apiPHP.get<CrosswordSource>(`/admin/passatempos/${id}/crossword`).then((r) => r.data);

export const previewCrossword = (
  payload: CrosswordDraftPayload,
): Promise<CrosswordPreviewResult> =>
  apiPHP
    .post<CrosswordPreviewResult>('/admin/passatempos/crossword/preview', payload)
    .then((r) => r.data);

export const createCrossword = (payload: CrosswordDraftPayload): Promise<CrosswordSaveResult> =>
  apiPHP.post<CrosswordSaveResult>('/admin/passatempos/crossword', payload).then((r) => r.data);

/** Cruzada tradicional (dicas numa lista): mesmo payload da direta, estilo explícito. */
export const createTraditionalCrossword = (
  payload: CrosswordDraftPayload,
): Promise<CrosswordSaveResult> =>
  apiPHP
    .post<CrosswordSaveResult>('/admin/passatempos/crossword', { ...payload, estilo: 'tradicional' })
    .then((r) => r.data);

export interface TraditionalCluesSource {
  id: number;
  titulo: string;
  largura: number;
  altura: number;
  estilosDePassatemposId: number | null;
  casasPretas: { x: number; y: number }[];
  palavras: {
    id: number;
    texto: string;
    x: number;
    y: number;
    direcao: boolean;
    chave: string;
  }[];
}

export const getTraditionalClues = (id: number): Promise<TraditionalCluesSource> =>
  apiPHP.get<TraditionalCluesSource>(`/admin/passatempos/${id}/chaves`).then((r) => r.data);

export const saveTraditionalClues = (
  id: number,
  body: { titulo: string; chaves: { palavraId: number; chave: string }[] },
): Promise<{ id: number }> =>
  apiPHP.put<{ id: number }>(`/admin/passatempos/${id}/chaves`, body).then((r) => r.data);

export const replaceCrossword = (
  id: number,
  payload: CrosswordDraftPayload,
): Promise<CrosswordSaveResult> =>
  apiPHP
    .put<CrosswordSaveResult>(`/admin/passatempos/${id}/crossword`, payload)
    .then((r) => r.data);

export const duplicatePassatempo = (id: number): Promise<{ id: number }> =>
  apiPHP.post<{ id: number }>(`/admin/passatempos/${id}/duplicate`).then((r) => r.data);

// ---- Caça-palavras (editor) ----
// O id_tipo do caça-palavras (o mesmo que o backend usa para recusar duplicar um jogo
// de outro tipo) vem de `TIPO_JOGO`, mais abaixo — fonte única para os ids da tabela
// `tipos_de_passatempos`.

export interface WordsearchSource {
  id: number;
  id_jogo: number;
  texto: string | null;
  game: unknown;
}

export interface WordsearchDraftPayload {
  titulo: string;
  game: IHuntWordData;
  texto: string;
}

export const getWordsearchSource = (id: number): Promise<WordsearchSource> =>
  apiPHP.get<WordsearchSource>(`/show_wordsearch/${id}`).then((r) => r.data);

/**
 * Erro do passo 2 (create_wordsearch) da criação do caça-palavras: a mensagem já
 * nomeia o jogo órfão e a ação certa (excluir #N) — é auto-suficiente. `orphanJogoId`
 * deixa quem pegar este erro (o `save` do módulo) reconhecer isso sem parsear a
 * string, e decidir não reembrulhar por cima com um conselho genérico que contradiz
 * o específico (ver modules/wordsearch/index.tsx).
 *
 * De propósito um `Error` comum, não uma subclasse: o `errMsg` do EditorShell só
 * confia na `.message` de um `Error` com `constructor === Error` — subclasse cairia
 * no fallback genérico e este texto acionável nunca chegaria à tela.
 */
export type OrphanWordsearchError = Error & { orphanJogoId: number };

export function isOrphanWordsearchError(err: unknown): err is OrphanWordsearchError {
  return (
    err instanceof Error &&
    err.constructor === Error &&
    typeof (err as OrphanWordsearchError).orphanJogoId === 'number'
  );
}

/**
 * Cria o jogo-pai e o blob. A API não tem rota única para isso — são dois POSTs, e
 * o primeiro já grava.
 *
 * Se o SEGUNDO falhar, o jogo-pai fica no banco sem grade: um órfão, que aparece no
 * catálogo de Jogos com o botão do editor e estoura ao abrir. O id dele está em
 * escopo aqui, então vai na mensagem — sem ele o admin recebe "deu erro" e não tem
 * o que fazer com a linha suja. Com ele, é só excluir aquele jogo.
 */
export const createWordsearchGame = async (
  payload: WordsearchDraftPayload,
): Promise<{ id: number }> => {
  const jogo = await apiPHP.post<{ id_jogo: number }>('/create_jogo', {
    titulo: payload.titulo,
    id_tipo: TIPO_JOGO.CACA_PALAVRAS,
  });
  const id = jogo.data.id_jogo;
  try {
    await apiPHP.post('/create_wordsearch', {
      id_jogo: id,
      game: payload.game,
      texto: payload.texto,
    });
  } catch (err: unknown) {
    // TS 4.5.5 não tem o `Error(message, { cause })` do ES2022 — o detalhe original
    // vai concatenado mesmo. `detailOf`, não `err.message` cru: um 400/500 do axios
    // tem `response.data.msg` do servidor, que `.message` sozinho jogaria fora.
    const detail = detailOf(err);
    const orphanError = new Error(
      `O jogo #${id} foi criado, mas a grade não foi gravada: ${detail}. ` +
        `Ele ficou vazio no catálogo de Jogos — exclua o jogo #${id} antes de tentar de novo ` +
        '(tentar de novo sem excluir cria outro).',
    ) as OrphanWordsearchError;
    orphanError.orphanJogoId = id;
    throw orphanError;
  }
  return { id };
};

export const replaceWordsearch = (id: number, payload: WordsearchDraftPayload): Promise<void> =>
  apiPHP
    .put(`/admin/jogos/${id}/wordsearch`, { game: payload.game, texto: payload.texto })
    .then(() => undefined);

/** Dry-run stateless — a rota NÃO tem :id. 422 vem com `{ action, erros }`. */
export const previewWordsearch = (payload: WordsearchDraftPayload): Promise<void> =>
  apiPHP
    .post('/admin/jogos/wordsearch/preview', { game: payload.game, texto: payload.texto })
    .then(() => undefined);

export const duplicateJogo = (id: number): Promise<{ id: number }> =>
  apiPHP.post<{ id: number }>(`/admin/jogos/${id}/duplicate`).then((r) => r.data);

// ---- Sudoku (editor) ----
// Espelha o caça-palavras acima, com UMA diferença que morde: o blob do sudoku
// trafega como STRING JSON, não como objeto (o `CreateSudokuDto` do backend é
// `@IsString()` e o player faz `JSON.parse(response.data.game)`). Quem serializa é o
// `wire.ts` do módulo; aqui `game` já chega string e é repassado como está.

export interface SudokuSource {
  id: number;
  id_jogo: number;
  /** STRING JSON do tabuleiro (o `show_sudoku` serializa antes de devolver). */
  game: unknown;
}

export interface SudokuDraftPayload {
  titulo: string;
  game: string;
}

export const getSudokuSource = (id: number): Promise<SudokuSource> =>
  apiPHP.get<SudokuSource>(`/show_sudoku/${id}`).then((r) => r.data);

/**
 * Cria o jogo-pai e o tabuleiro. Como no caça-palavras, a API não tem rota única:
 * são dois POSTs e o primeiro já grava. Se o SEGUNDO falhar, o jogo fica no catálogo
 * sem tabuleiro e estoura ao abrir — por isso o id dele vai na mensagem, que é o que
 * o admin precisa para limpar a linha suja.
 */
export const createSudokuGame = async (payload: SudokuDraftPayload): Promise<{ id: number }> => {
  const jogo = await apiPHP.post<{ id_jogo: number }>('/create_jogo', {
    titulo: payload.titulo,
    id_tipo: TIPO_JOGO.SUDOKU,
  });
  const id = jogo.data.id_jogo;
  try {
    await apiPHP.post('/create_sudoku', { id_jogo: id, game: payload.game });
  } catch (err: unknown) {
    // TS 4.5.5 não tem o `Error(message, { cause })` do ES2022 — o detalhe original
    // vai concatenado mesmo.
    const detail = err instanceof Error ? err.message : String(err);
    throw new Error(
      `O jogo #${id} foi criado, mas o tabuleiro não foi gravado: ${detail}. ` +
        `Ele ficou vazio no catálogo de Jogos — exclua o jogo #${id} antes de tentar de novo ` +
        '(tentar de novo sem excluir cria outro).',
    );
  }
  return { id };
};

export const replaceSudoku = (id: number, payload: SudokuDraftPayload): Promise<void> =>
  apiPHP.put(`/admin/jogos/${id}/sudoku`, { game: payload.game }).then(() => undefined);

/** Dry-run stateless — a rota NÃO tem :id. 422 vem com a lista de erros do domínio. */
export const previewSudoku = (payload: SudokuDraftPayload): Promise<void> =>
  apiPHP.post('/admin/jogos/sudoku/preview', { game: payload.game }).then(() => undefined);

/**
 * ⚠️ NÃO troque por `duplicateJogo`. Apesar do nome genérico, aquele bate em
 * `POST /admin/jogos/:id/duplicate`, que é servido pelo controller do caça-palavras
 * e tem guard de tipo — chamá-lo para um sudoku devolve 404. O sudoku tem rota
 * própria.
 */
export const duplicateSudoku = (id: number): Promise<{ id: number }> =>
  apiPHP.post<{ id: number }>(`/admin/jogos/${id}/sudoku/duplicate`).then((r) => r.data);

// ---- Jogos ----
export interface JogoAdmin {
  idJogo: number;
  titulo: string | null;
  subtitulo: string | null;
  edicoesId: number | null;
  idTema: number | null;
  idTipo: number | null;
  idDificuldade: number | null;
  doDia: string | null;
  cognitiveDomain?: CognitiveDomain | null;
  academyPoints?: number | null;
}

export interface JogoUpdatePayload {
  titulo?: string;
  subtitulo?: string;
  textoIntro?: string;
  edicoesId?: number;
  idTema?: number;
  idTipo?: number;
  idDificuldade?: number;
  ordemJogo?: number;
  doDia?: string;
  cognitiveDomain?: CognitiveDomain | null;
  academyPoints?: number | null;
}

export interface ListJogosParams {
  page?: number;
  pageSize?: number;
  q?: string;
  edicoesId?: number;
  idTema?: number;
  idTipo?: number;
}

export const listJogos = (params: ListJogosParams = {}): Promise<PaginatedResult<JogoAdmin>> =>
  apiPHP.get<PaginatedResult<JogoAdmin>>('/admin/jogos', { params }).then((r) => r.data);

export const updateJogo = (id: number, payload: JogoUpdatePayload): Promise<JogoAdmin> =>
  apiPHP.patch<JogoAdmin>(`/admin/jogos/${id}`, payload).then((r) => r.data);

export const deleteJogo = (id: number): Promise<void> =>
  apiPHP.delete(`/admin/jogos/${id}`).then(() => undefined);

// ---- Regerar (sudoku / caça-palavras) ----
// A cruzada tem o seu próprio wrapper (`regenerateCrossword`, acima), porque mora em
// /admin/passatempos e devolve `naoColocadas`.

/**
 * Ids da tabela `tipos_de_passatempos` — a fonte única desses números no portal (não
 * espalhar `7` e `8` soltos pelo JSX nem pelos payloads). Só CACA_PALAVRAS e SUDOKU
 * têm endpoint de regenerate (rébus e quiz não têm).
 *
 * Declarado DEPOIS de quem o usa (`createWordsearchGame`, aqui, e
 * `modules/wordsearch/index.tsx`, que o lê no topo do módulo, ao montar o `entry`).
 * É seguro porque não há ciclo de import: `services/admin` não importa nada de
 * `pages/`, então quando o módulo do editor é avaliado este arquivo já rodou inteiro
 * e o const está inicializado. (Não é o argumento de antes — "o uso é dentro do corpo
 * da função" — que deixou de ser verdade quando o `entry` passou a ler `TIPO_JOGO`
 * em avaliação de topo de módulo.)
 */
export const TIPO_JOGO = {
  REBUS: 5,
  QUIZ: 6,
  CACA_PALAVRAS: 7,
  SUDOKU: 8,
} as const;

export type SudokuDificuldade = 'facil' | 'medio' | 'dificil';

/**
 * Regera a grade do sudoku mantendo o mesmo jogo (edição, tema e título ficam
 * intactos). Sem `dificuldade`, o backend mantém a atual — mas o corpo vai como `{}`,
 * e não `undefined`, senão o POST sai sem body e o ValidationPipe recusa.
 */
export const regenerateSudoku = (
  id: number,
  body: { dificuldade?: SudokuDificuldade } = {},
): Promise<{ id_jogo: number }> =>
  apiPHP.post(`/admin/jogos/${id}/regenerate-sudoku`, body).then((r) => r.data);

/**
 * Regera o caça-palavras a partir de uma lista nova de palavras, mantendo o mesmo jogo.
 * O backend normaliza as palavras e devolve as que **efetivamente** foram posicionadas
 * — pode ser menos do que se enviou.
 */
export const regenerateWordsearch = (
  id: number,
  body: { palavras: string[]; tamanho?: number },
): Promise<{ id_jogo: number; palavras: string[] }> =>
  apiPHP.post(`/admin/jogos/${id}/regenerate-wordsearch`, body).then((r) => r.data);
// ---- Torneios ----
// Leitura usa as rotas públicas (autenticadas) /torneios; escrita usa /admin/tournaments.
export type TorneioStatus = 'futuro' | 'ativo' | 'encerrado';

/**
 * Visual do torneio no app (troféu + cor da mancha). Espelha o enum fechado da
 * API — valor fora da lista é rejeitado com 400. Sem tipo → troféu roxo no app.
 * Os RÓTULOS em pt-BR são copy de UI e moram na página do admin.
 */
export type TorneioType = 'golden_owl' | 'purple_trophy' | 'green_trophy';

export interface TorneioListItem {
  id: number;
  name: string;
  description: string | null;
  startsAt: string;
  endsAt: string;
  multiplier: number;
  type: TorneioType | null;
  status: TorneioStatus;
  itemsCount: number;
  participantsCount: number;
  joined: boolean;
}

export interface TorneioItem {
  id: number;
  kind: 'passatempo' | 'jogo';
  refId: number;
  tipoId: number | null;
  titulo: string | null;
  subtitulo: string | null;
  ordem: number | null;
}

export interface TorneioDetail {
  id: number;
  name: string;
  description: string | null;
  startsAt: string;
  endsAt: string;
  multiplier: number;
  type: TorneioType | null;
  status: TorneioStatus;
  participantsCount: number;
  joined: boolean;
  finalized: boolean;
  myFinalPosition: number | null;
  myFinalScore: number | null;
  items: TorneioItem[];
}

/** Um jogo da biblioteca, elegível para compor um torneio. `kind` desambigua o `id`. */
export interface PickableGame {
  kind: 'passatempo' | 'jogo';
  id: number;
  titulo: string | null;
  subtitulo: string | null;
  tipoId: number | null;
  edicaoId: number | null;
  edicaoNome: string | null;
  cognitiveDomain?: CognitiveDomain | null;
  academyPoints?: number | null;
}

/** Exatamente um dos dois — o backend valida o XOR. */
export type TorneioItemInput = { passatemposId: number } | { jogosId: number };

export interface TorneioMetaPayload {
  name: string;
  description?: string;
  startsAt: string;
  endsAt: string;
  multiplier: number;
  type?: TorneioType;
}

export interface CreateTorneioFromGamesPayload extends TorneioMetaPayload {
  items: TorneioItemInput[];
}

export type UpdateTorneioPayload = Partial<TorneioMetaPayload>;

/** Resposta dos endpoints de escrita. Não ecoa `description` — recarregue a lista. */
export interface TorneioAdminResult {
  id: number;
  name: string;
  startsAt: string;
  endsAt: string;
  multiplier: number;
  type: TorneioType | null;
  finalizedAt: string | null;
}

export interface PickableGamesQuery {
  edicaoId?: number;
  semEdicao?: boolean;
  q?: string;
  edicaoKind?: EditionKind;
}

export const listTorneios = (status?: TorneioStatus): Promise<TorneioListItem[]> =>
  apiPHP
    .get<TorneioListItem[]>('/torneios', { params: status === undefined ? {} : { status } })
    .then((r) => r.data);

export const getTorneio = (id: number): Promise<TorneioDetail> =>
  apiPHP.get<TorneioDetail>(`/torneios/${id}`).then((r) => r.data);

export const listPickableGames = (query: PickableGamesQuery): Promise<PickableGame[]> => {
  // Só manda o que foi preenchido: `semEdicao=false` ou `q=''` viraria filtro à toa.
  const params: Record<string, string | number | boolean> = {};
  if (query.edicaoId !== undefined) params.edicaoId = query.edicaoId;
  if (query.semEdicao) params.semEdicao = true;
  if (query.q) params.q = query.q;
  if (query.edicaoKind) params.edicaoKind = query.edicaoKind;
  return apiPHP.get<PickableGame[]>('/admin/tournaments/games', { params }).then((r) => r.data);
};

/** Cria COPIANDO os jogos escolhidos (isola o ranking do torneio do jogo original). */
export const createTorneioFromGames = (
  payload: CreateTorneioFromGamesPayload,
): Promise<TorneioAdminResult> =>
  apiPHP.post<TorneioAdminResult>('/admin/tournaments/from-games', payload).then((r) => r.data);

/** Atalho: copia TODOS os jogos de uma edição para um torneio novo. */
export const createTorneioFromEdicao = (
  edicaoId: number,
  payload: TorneioMetaPayload,
): Promise<TorneioAdminResult> =>
  apiPHP
    .post<TorneioAdminResult>(`/admin/tournaments/from-edicao/${edicaoId}`, payload)
    .then((r) => r.data);

/** Só metadados (nome, descrição, datas, multiplicador). Trocar itens é fora do escopo v1. */
export const updateTorneio = (
  id: number,
  payload: UpdateTorneioPayload,
): Promise<TorneioAdminResult> =>
  apiPHP.patch<TorneioAdminResult>(`/admin/tournaments/${id}`, payload).then((r) => r.data);

// ---- Desafio diário ----
// Escrita copia os jogos; no PUT o item pode ser original ou cópia deste desafio.

export type DailyChallengeItemKind = 'passatempo' | 'jogo';

export interface DailyChallengeItem {
  position: number;
  kind: DailyChallengeItemKind;
  id: number;
  tipoId: number | null;
  titulo: string | null;
}

export interface DailyChallenge {
  id: number;
  date: string;
  editable: boolean;
  items: DailyChallengeItem[];
}

export type DailyChallengeItemInput = { passatemposId: number } | { jogosId: number };

export interface DailyChallengePayload {
  date: string;
  items: DailyChallengeItemInput[];
}

export const listDailyChallenges = (range: { from: string; to: string }): Promise<DailyChallenge[]> =>
  apiPHP.get<DailyChallenge[]>('/admin/daily-challenges', { params: range }).then((r) => r.data);

export const getDailyChallenge = (id: number): Promise<DailyChallenge> =>
  apiPHP.get<DailyChallenge>(`/admin/daily-challenges/${id}`).then((r) => r.data);

export const createDailyChallenge = (payload: DailyChallengePayload): Promise<DailyChallenge> =>
  apiPHP.post<DailyChallenge>('/admin/daily-challenges', payload).then((r) => r.data);

export const updateDailyChallenge = (
  id: number,
  payload: DailyChallengePayload,
): Promise<DailyChallenge> =>
  apiPHP.put<DailyChallenge>(`/admin/daily-challenges/${id}`, payload).then((r) => r.data);

export const deleteDailyChallenge = (id: number): Promise<void> =>
  apiPHP.delete(`/admin/daily-challenges/${id}`).then(() => undefined);

// ---- Treinos da Academia da Mente ----
// Um treino por dia, com 1 a 5 jogos de quaisquer áreas; `missingDomains` aponta as áreas sem jogo.

export interface MindTrainingItem {
  position: number;
  kind: 'passatempo' | 'jogo';
  id: number;
  tipoId: number | null;
  titulo: string | null;
  domain: CognitiveDomain | null;
  points: number | null;
  edicaoId: number | null;
  edicaoNome: string | null;
}

export interface MindTraining {
  id: number;
  date: string;
  editable: boolean;
  items: MindTrainingItem[];
  missingDomains: CognitiveDomain[];
}

export interface MindTrainingPayload {
  date: string;
  items: DailyChallengeItemInput[];
}

export const listMindTrainings = (range: { from: string; to: string }): Promise<MindTraining[]> =>
  apiPHP.get<MindTraining[]>('/admin/mind-trainings', { params: range }).then((r) => r.data);

export const createMindTraining = (payload: MindTrainingPayload): Promise<MindTraining> =>
  apiPHP.post<MindTraining>('/admin/mind-trainings', payload).then((r) => r.data);

export const updateMindTraining = (id: number, payload: MindTrainingPayload): Promise<MindTraining> =>
  apiPHP.put<MindTraining>(`/admin/mind-trainings/${id}`, payload).then((r) => r.data);

export const deleteMindTraining = (id: number): Promise<void> =>
  apiPHP.delete(`/admin/mind-trainings/${id}`).then(() => undefined);

// ---- Recompensas ----
// Baú de recompensas: sorteio (definições com peso) e marcos de sequência (7/30 dias).

export type RewardRarity = 'common' | 'uncommon' | 'rare' | 'legendary';

export interface RewardDefinition {
  id: number;
  name: string;
  rarity: RewardRarity;
  weight: number;
  active: boolean;
  hintCards: number;
  streakProtectors: number;
  /** % com 1 casa, calculada pela API (0 se inativo ou soma 0). */
  chance: number;
}

export type RewardDefinitionPayload = Omit<RewardDefinition, 'id' | 'chance'>;

export interface ChestMilestone {
  streakDays: number;
  passDays: number;
  hintCards: number;
  streakProtectors: number;
  subscriberHintCards: number;
  subscriberStreakProtectors: number;
}

export type ChestMilestonePayload = Omit<ChestMilestone, 'streakDays'>;

export const listRewardDefinitions = (): Promise<RewardDefinition[]> =>
  apiPHP.get<RewardDefinition[]>('/admin/rewards/definitions').then((r) => r.data);

export const createRewardDefinition = (payload: RewardDefinitionPayload): Promise<RewardDefinition> =>
  apiPHP.post<RewardDefinition>('/admin/rewards/definitions', payload).then((r) => r.data);

export const updateRewardDefinition = (
  id: number,
  payload: RewardDefinitionPayload,
): Promise<RewardDefinition> =>
  apiPHP.put<RewardDefinition>(`/admin/rewards/definitions/${id}`, payload).then((r) => r.data);

export const listChestMilestones = (): Promise<ChestMilestone[]> =>
  apiPHP.get<ChestMilestone[]>('/admin/rewards/milestones').then((r) => r.data);

export const updateChestMilestone = (
  streakDays: number,
  payload: ChestMilestonePayload,
): Promise<ChestMilestone> =>
  apiPHP.put<ChestMilestone>(`/admin/rewards/milestones/${streakDays}`, payload).then((r) => r.data);

// ---- Rébus (editor) ----
// O rébus não tem grade nem blob: são três campos e uma imagem. As esquisitices da
// API dele (o verbo PATCH em vez de PUT, o `show_rebus` que devolve ARRAY, o upload
// multipart) ficam presas aqui e no `wire.ts`/`upload.ts` do módulo — o shell não
// aprende nenhuma delas.

/** Uma linha da tabela `rebus`, como o `show_rebus` devolve (campos nuláveis). */
export interface RebusSource {
  id: number;
  id_jogo: number;
  numeracao: string | null;
  solucao: string | null;
  imagem: string | null;
}

/** Corpo do replace — o que a tabela `rebus` guarda, sem o título (que é do jogo). */
export interface RebusWritePayload {
  numeracao: string;
  solucao: string;
  imagem: string;
}

/**
 * O rébus de um jogo, ou `null` se o jogo existe mas ainda não tem rébus (é o estado
 * de um jogo criado pela metade). `GET /show_rebus/:id` é read público e devolve um
 * ARRAY — a rota é do legado e não tem versão /admin.
 */
export const getRebusSource = (idJogo: number): Promise<RebusSource | null> =>
  apiPHP
    .get<RebusSource[]>(`/show_rebus/${idJogo}`)
    .then((r) => (Array.isArray(r.data) ? r.data[0] ?? null : null));

/**
 * Cria o jogo-pai e o rébus. Como no caça-palavras, a API não tem rota única: são
 * dois POSTs e o primeiro já grava. Se o SEGUNDO falhar, o jogo fica no catálogo sem
 * rébus e estoura ao abrir — o id vai na mensagem para o admin saber qual excluir.
 */
export const createRebusGame = async (
  payload: { titulo: string } & RebusWritePayload,
): Promise<{ id: number }> => {
  const jogo = await apiPHP.post<{ id_jogo: number }>('/create_jogo', {
    titulo: payload.titulo,
    id_tipo: TIPO_JOGO.REBUS,
  });
  const id = jogo.data.id_jogo;
  try {
    await apiPHP.post('/create_rebus', {
      id_jogo: id,
      numeracao: payload.numeracao,
      solucao: payload.solucao,
      // A rota aceita `imagem` opcional: um rébus novo pode nascer sem ela, porque
      // o upload só é possível DEPOIS de o jogo existir (a rota de imagem é por id).
      imagem: payload.imagem === '' ? undefined : payload.imagem,
    });
  } catch (err: unknown) {
    // TS 4.5.5 não tem o `Error(message, { cause })` do ES2022 — o detalhe original
    // vai concatenado mesmo.
    const detail = err instanceof Error ? err.message : String(err);
    throw new Error(
      `O jogo #${id} foi criado, mas o rébus não foi gravado: ${detail}. ` +
        `Ele ficou vazio no catálogo de Jogos — exclua o jogo #${id} antes de tentar de novo ` +
        '(tentar de novo sem excluir cria outro).',
    );
  }
  return { id };
};

/**
 * Replace transacional do rébus do jogo. É **PATCH**, não PUT: a rota já existia e já
 * fazia replace, e criar um PUT novo só para espelhar o verbo do caça-palavras
 * deixaria duas rotas fazendo a mesma coisa (§6.1 do design da Fase 4).
 *
 * A resposta é ignorada de propósito — hoje o controller responde 204 e o design
 * prevê `200 { id_jogo }`; quem chama já tem o id na mão e não depende disso.
 */
export const replaceRebus = (idJogo: number, payload: RebusWritePayload): Promise<void> =>
  apiPHP.patch(`/admin/jogos/${idJogo}/rebus`, payload).then(() => undefined);

/**
 * Duplica um rébus bloqueado (com ranking) numa cópia editável.
 *
 * ⚠️ NÃO é o `duplicateJogo` daqui de cima: apesar do nome genérico, aquele bate em
 * `POST /admin/jogos/:id/duplicate`, que é servido pelo controller do CAÇA-PALAVRAS e
 * recusa jogo de outro tipo com 404. O rébus tem rota própria.
 */
export const duplicateRebus = (idJogo: number): Promise<{ id: number }> =>
  apiPHP.post<{ id: number }>(`/admin/jogos/${idJogo}/rebus/duplicate`).then((r) => r.data);

/**
 * Sobe a imagem do rébus (multipart, campo `imagem`) e devolve a URL pública.
 * Precisa do jogo já criado — o id vai na rota e compõe a key no S3.
 *
 * `onProgress` recebe 0..100. O axios só sabe a porcentagem quando o browser informa
 * o total (`lengthComputable`); sem isso o callback não é chamado, e quem desenha a
 * barra trata como indeterminado.
 */
export const uploadRebusImagem = (
  idJogo: number,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<{ url: string }> => {
  const form = new FormData();
  form.append('imagem', file);
  return apiPHP
    .post<{ url: string }>(`/admin/jogos/${idJogo}/rebus/imagem`, form, {
      // Sem Content-Type explícito: o browser precisa inserir o `boundary` do
      // multipart, e escrever o header à mão o apaga.
      onUploadProgress: (e: ProgressEvent) => {
        if (!onProgress || !e.lengthComputable || !e.total) return;
        onProgress(Math.round((e.loaded / e.total) * 100));
      },
    })
    .then((r) => r.data);
};

/** Rascunho de cruzada direta vindo do import (spec §4.2): o `CrosswordDraftPayload`
 *  sem título/tipo — o admin escolhe no editor. Slots vazios vêm `null`. */
export interface ImportedCrosswordDraft {
  largura: number;
  altura: number;
  palavras: CrosswordWordPayload[];
  casasMortas: {
    x: number;
    y: number;
    visivel: boolean;
    palavra1Ref: string | null;
    palavra2Ref: string | null;
  }[];
  casasDestaques: CrosswordHighlightPayload[];
}

export type ImportResult =
  | { kind: 'crossword-direct'; titulo: string; draft: ImportedCrosswordDraft; warnings: string[] }
  | {
      kind: 'crossword-traditional';
      titulo: string;
      draft: ImportedCrosswordDraft;
      warnings: string[];
    }
  | { kind: 'sudoku'; titulo: string; game: unknown; warnings: string[] };

export type ImportKind = ImportResult['kind'];

/**
 * Envia o XML exportado pelo Crossword Compiler e recebe o RASCUNHO do jogo — o
 * servidor não grava nada; quem grava é o "Salvar" do editor. Erros: 400 (arquivo
 * ilegível), 413 (> 1 MB), 422 `{ message, erros }` (tipo não suportado ou inválido).
 */
export const importCrosswordCompiler = (file: File): Promise<ImportResult> => {
  const form = new FormData();
  form.append('arquivo', file);
  // Sem Content-Type explícito: o browser insere o boundary do multipart.
  return apiPHP.post<ImportResult>('/admin/import/crossword-compiler', form).then((r) => r.data);
};

export type CatalogKind =
  | 'crossword-direct'
  | 'crossword-traditional'
  | 'wordsearch'
  | 'sudoku'
  | 'rebus'
  | 'quiz'
  | 'other';

export type CatalogItem =
  | {
      source: 'passatempo';
      kind: CatalogKind;
      edicaoNome: string | null;
      temaNome: string | null;
      createdAt: string | null;
      passatempo: PassatempoAdmin;
    }
  | {
      source: 'jogo';
      kind: CatalogKind;
      edicaoNome: string | null;
      temaNome: string | null;
      createdAt: string | null;
      jogo: JogoAdmin;
    };

export interface ListCatalogParams {
  page?: number;
  pageSize?: number;
  q?: string;
  id?: number;
  edicoesId?: number;
  temaId?: number;
  dificuldadeId?: number;
  kind?: Exclude<CatalogKind, 'other'>;
}

/** Catálogo unificado (passatempos + jogos) paginado no servidor. */
export const listCatalog = (params: ListCatalogParams = {}): Promise<PaginatedResult<CatalogItem>> =>
  apiPHP.get<PaginatedResult<CatalogItem>>('/admin/catalog', { params }).then((r) => r.data);

/** Busca edições no servidor (mais recentes primeiro), para seletores com busca. */
export const searchEdicoes = (params: { q?: string; limit: number }): Promise<EdicaoAdmin[]> =>
  apiPHP.get<EdicaoAdmin[]>('/admin/edicoes', { params }).then((r) => r.data);

/** Busca temas no servidor, opcionalmente de uma edição. */
export const searchTemas = (params: {
  edicoesId?: number;
  q?: string;
  limit: number;
}): Promise<Tema[]> => apiPHP.get<Tema[]>('/jogo/temas', { params }).then((r) => r.data);

// ---- Coleção em destaque (fila do app) ----
export interface FeaturedCollectionAdmin {
  id: number;
  ordem: number;
  imageUrl: string;
  edicao: {
    id: number;
    nome: string | null;
    free: boolean;
    eligible: boolean;
    /** Preço em centavos (`valorAVista`). */
    price: number | null;
  };
}

export const listFeaturedCollections = (): Promise<FeaturedCollectionAdmin[]> =>
  apiPHP.get<FeaturedCollectionAdmin[]>('/admin/featured-collections').then((r) => r.data);

export const createFeaturedCollection = (
  edicaoId: number,
  imageUrl: string,
): Promise<FeaturedCollectionAdmin> =>
  apiPHP
    .post<FeaturedCollectionAdmin>('/admin/featured-collections', { edicaoId, imageUrl })
    .then((r) => r.data);

export const updateFeaturedCollectionImage = (
  id: number,
  imageUrl: string,
): Promise<FeaturedCollectionAdmin> =>
  apiPHP
    .patch<FeaturedCollectionAdmin>(`/admin/featured-collections/${id}`, { imageUrl })
    .then((r) => r.data);

export const reorderFeaturedCollections = (ids: number[]): Promise<void> =>
  apiPHP.put('/admin/featured-collections/ordem', { ids }).then(() => undefined);

export const deleteFeaturedCollection = (id: number): Promise<void> =>
  apiPHP.delete(`/admin/featured-collections/${id}`).then(() => undefined);

/** Foto do destaque: multipart `imagem`, sem Content-Type (o browser põe o boundary). */
export const uploadFeaturedImage = (
  file: File,
  onProgress?: (percent: number) => void,
): Promise<{ url: string }> => {
  const form = new FormData();
  form.append('imagem', file);
  return apiPHP
    .post<{ url: string }>('/admin/featured-collections/imagem', form, {
      onUploadProgress: (e: ProgressEvent) => {
        if (!onProgress || !e.lengthComputable || !e.total) return;
        onProgress(Math.round((e.loaded / e.total) * 100));
      },
    })
    .then((r) => r.data);
};
